/**
 * spreadsheetSelection.js — pure helpers for mouse-drag range selection and
 * formula "point mode" ref insertion, used by SpreadsheetSheet.js (T-049
 * follow-up: Excel-style range selection + click-to-insert-ref).
 *
 * No React here on purpose: these are the two bits of range math worth unit
 * testing in isolation. Reuses spreadsheetEngine's col/row parsing so the
 * ref format (A1, $A$1) stays defined in exactly one place.
 */
import { _internals } from './spreadsheetEngine';

const { parseRef, numToCol } = _internals;

/**
 * Normalize an anchor/focus cell pair into an Excel-style ref string.
 * Same cell -> 'A1'. Different cells -> 'A1:B3' (min -> max, both axes).
 */
export function rangeRefString(anchor, focus) {
  if (!anchor) return '';
  if (!focus || anchor === focus) return anchor;
  const a = parseRef(anchor);
  const f = parseRef(focus);
  if (!a || !f) return anchor; // ponytail: malformed ref, fall back to anchor as-is
  const c1 = Math.min(a.col, f.col);
  const c2 = Math.max(a.col, f.col);
  const r1 = Math.min(a.row, f.row);
  const r2 = Math.max(a.row, f.row);
  const start = numToCol(c1) + r1;
  const end = numToCol(c2) + r2;
  return start === end ? start : `${start}:${end}`;
}

// Techo duro de expansión = grid máximo permitido (MAX_ROWS×MAX_COLS de
// SpreadsheetSheet). Un ref TEXTUAL en una fórmula a medio teclear (p. ej.
// "A1:C9999999") no está acotado por el grid; sin este clamp, expandirlo
// materializaba millones de llaves por render hasta reventar el Map de
// coloreado ("Map maximum size exceeded", T-080).
const MAX_EXPAND_ROWS = 200;
const MAX_EXPAND_COLS = 52;

/** All cell keys inside the anchor..focus rectangle (inclusive), row-major.
 * Clamped to the maximum grid size — out-of-grid tails are dropped. */
export function cellsInRange(anchor, focus) {
  const a = parseRef(anchor);
  const f = parseRef(focus || anchor);
  if (!a || !f) return anchor ? [anchor] : [];
  const c1 = Math.min(a.col, f.col);
  const c2 = Math.min(Math.max(a.col, f.col), MAX_EXPAND_COLS);
  const r1 = Math.min(a.row, f.row);
  const r2 = Math.min(Math.max(a.row, f.row), MAX_EXPAND_ROWS);
  const out = [];
  for (let r = r1; r <= r2; r++) {
    for (let c = c1; c <= c2; c++) {
      out.push(numToCol(c) + r);
    }
  }
  return out;
}
